import { useState } from "react";
import { X, KeyRound, Check, Eye, EyeOff } from "lucide-react";
import { changePassword, apiErrorMessage } from "../lib/api";

export default function ChangePasswordModal({ open, onClose, t }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const reset = () => { setCur(""); setNext(""); setConfirm(""); setErr(""); setDone(false); setBusy(false); };
  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 6) { setErr(t("pwdTooShort")); return; }
    if (next !== confirm) { setErr(t("pwdMismatch")); return; }
    setBusy(true);
    try {
      await changePassword(cur, next);
      setDone(true);
      setTimeout(close, 1400);
    } catch (e2) {
      setErr(apiErrorMessage(e2));
      setBusy(false);
    }
  };

  if (!open) return null;
  const inputType = show ? "text" : "password";
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="change-password-modal">
      <div className="bg-white rounded-3xl p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-1">
          <h3 className="font-display font-bold text-2xl text-slate-800 inline-flex items-center gap-2"><KeyRound size={22} className="text-teal-600" /> {t("changePassword")}</h3>
          <button data-testid="pwd-close" onClick={close} className="text-slate-400 hover:text-slate-600"><X size={22} /></button>
        </div>
        {done ? (
          <div data-testid="pwd-success" className="py-8 text-center">
            <div className="w-16 h-16 mx-auto grid place-items-center bg-teal-100 text-teal-600 rounded-full mb-3"><Check size={32} /></div>
            <p className="font-bold text-slate-700">{t("pwdChanged")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-3">
            <label className="text-sm font-bold text-slate-500">{t("currentPassword")}</label>
            <input data-testid="pwd-current" type={inputType} value={cur} onChange={(e) => setCur(e.target.value)} autoFocus
              className="w-full mt-1 mb-3 px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-teal-400 outline-none font-semibold" />
            <label className="text-sm font-bold text-slate-500">{t("newPassword")}</label>
            <input data-testid="pwd-new" type={inputType} value={next} onChange={(e) => setNext(e.target.value)}
              className="w-full mt-1 mb-3 px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-teal-400 outline-none font-semibold" />
            <label className="text-sm font-bold text-slate-500">{t("confirmPassword")}</label>
            <input data-testid="pwd-confirm" type={inputType} value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full mt-1 px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-teal-400 outline-none font-semibold" />
            <button type="button" onClick={() => setShow((v) => !v)} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-700">
              {show ? <EyeOff size={15} /> : <Eye size={15} />} {show ? t("hide") : t("show")}
            </button>
            {err && <div data-testid="pwd-error" className="mt-3 text-sm font-semibold text-red-600 bg-red-50 rounded-xl px-3 py-2">{err}</div>}
            <button data-testid="pwd-submit" type="submit" disabled={busy}
              className="mt-4 w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-bold py-3 rounded-2xl inline-flex items-center justify-center gap-2">
              <KeyRound size={18} /> {busy ? "…" : t("updatePassword")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
